import { Markdown } from "@sagot/chat";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, cn, Icon, type IconName } from "@sagot/ui";
import type { ReactNode } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { ConversationAvatar } from "./conversation-avatar";
import type { MessageView } from "./inbox-model";

const LOW_CONFIDENCE = 0.6;
const HIGH_CONFIDENCE = 0.8;
const MS_PER_SECOND = 1000;

const renderCitation = (n: number, key: string) => (
  <span key={key} className="cite">
    {n}
  </span>
);

function Citations({ message }: { message: MessageView }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  if (message.citations.length === 0) return null;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      {message.citations.map((c) => {
        const page = typeof c.location.page === "number" ? ` · ${t("engage.inbox.thread.location", { page: c.location.page })}` : "";
        const label = `${c.title ?? t("engage.inbox.thread.citationFallback", { n: c.n })}${page}`;
        const chip = "inline-flex items-center gap-1 rounded-sm border border-line bg-surface px-1.5 py-0.5 text-[11px] font-semibold text-fg-muted";
        return c.source_id ? (
          <AppLink key={`${String(c.n)}-${c.source_id}`} href={consoleRoutes.sourceDetail(summary.slug, c.source_id)} className={cn(chip, "hover:border-primary hover:text-primary-ink")}>
            <Icon name="file-text" className="size-3" />
            {label}
          </AppLink>
        ) : (
          <span key={String(c.n)} className={chip}>
            <Icon name="quote" className="size-3" />
            {label}
          </span>
        );
      })}
    </div>
  );
}

function confidenceTone(value: number): string {
  if (value < LOW_CONFIDENCE) return "text-warning";
  return value >= HIGH_CONFIDENCE ? "text-success" : "text-fg-muted";
}

export function UserBubble({ message, name }: { message: MessageView; name: string }) {
  const fmt = useFormatters();
  return (
    <div className="flex items-end gap-2">
      <ConversationAvatar name={name} size="sm" />
      <div className="max-w-[80%]">
        <div className="whitespace-pre-wrap rounded-lg rounded-bl-xs border border-line bg-surface px-3.5 py-2.5 text-sm leading-relaxed">{message.content}</div>
        <div className="mt-1 text-[11px] text-fg-subtle">{fmt.time(message.created_at)}</div>
      </div>
    </div>
  );
}

function Meta({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <div className="mt-1 flex flex-wrap items-center justify-end gap-1.5 text-[11px] text-fg-subtle">
      <Icon name={icon} className="size-3" />
      {children}
    </div>
  );
}

export function AssistantBubble({ message }: { message: MessageView }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const parts = [t("engage.inbox.thread.aiMeta")];
  if (message.latency_ms !== null) parts.push(t("engage.inbox.thread.seconds", { value: (message.latency_ms / MS_PER_SECOND).toFixed(1) }));
  if (message.citations.length) parts.push(t("engage.inbox.thread.sources", { count: message.citations.length }));
  return (
    <div className="flex flex-col items-end">
      <div className="max-w-[80%] rounded-lg rounded-br-xs bg-primary-soft px-3.5 py-2.5 text-sm leading-relaxed text-fg">
        <Markdown source={message.content} renderCitation={renderCitation} />
        <Citations message={message} />
      </div>
      <Meta icon="bot">
        <span>{parts.join(" · ")}</span>
        {message.confidence !== null ? (
          <>
            <span aria-hidden="true">·</span>
            <span className={cn("font-semibold", confidenceTone(message.confidence))}>{t("engage.inbox.thread.confidence", { value: fmt.percent(message.confidence) })}</span>
          </>
        ) : null}
        <span aria-hidden="true">·</span>
        <span>{fmt.time(message.created_at)}</span>
      </Meta>
    </div>
  );
}

function DeliveryStatus({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (status === "outside_window") return <span className="font-semibold text-danger">{t("engage.inbox.thread.outsideWindow")}</span>;
  if (status === "queued" || status === "pending_send") return <span>{t("engage.inbox.thread.queued")}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <Icon name="check" className="size-3 text-success" />
      {t("engage.inbox.thread.delivered")}
    </span>
  );
}

export function StaffBubble({ message }: { message: MessageView }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const name = message.author_name ?? t("engage.inbox.thread.staffMember");
  return (
    <div className="flex flex-col items-end" data-role="staff">
      <div className="bubble-user max-w-[80%] whitespace-pre-wrap">{message.content}</div>
      <Meta icon="headset">
        <span>{t("engage.inbox.thread.staffMeta", { name })}</span>
        <span aria-hidden="true">·</span>
        <span>{fmt.time(message.created_at)}</span>
        <span aria-hidden="true">·</span>
        <DeliveryStatus status={message.delivery_status} />
      </Meta>
    </div>
  );
}

export function NoteCard({ message }: { message: MessageView }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const name = message.author_name ?? t("engage.inbox.thread.staffMember");
  return (
    <div className="mx-auto max-w-[90%] rounded-lg border border-accent bg-accent-soft px-3.5 py-2.5" data-role="note" data-label={message.label ?? undefined}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold text-accent-ink">
        <Icon name={message.label ? "sparkles" : "lock"} className="size-3" />
        {message.label
          ? t("engage.inbox.thread.labelledNoteHeading", { label: message.label, name, time: fmt.time(message.created_at) })
          : t("engage.inbox.thread.noteHeading", { name, time: fmt.time(message.created_at) })}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-fg">{message.content}</p>
    </div>
  );
}

const PILL_TONE = { danger: "text-danger", primary: "border-primary bg-primary-soft text-primary-ink", muted: "text-fg-muted", success: "text-success" } as const;

export function SystemDivider({ icon, tone, children }: { icon: IconName; tone: keyof typeof PILL_TONE; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="divider flex-1" />
      <span className={cn("inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface px-3 py-1 text-center text-[11px] font-semibold", PILL_TONE[tone])}>
        <Icon name={icon} className="size-3.5 shrink-0" />
        {children}
      </span>
      <div className="divider flex-1" />
    </div>
  );
}
